import { pagesPath } from "faqSrc/lib/$path";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { errorWithoutThrowInProduction } from "shared/lib/logging/logHelper";
import UserReactionIconButton from "~/components/modules/userReactionButtons/IconButton";
import UserReactionWriteCommentButton, {
  HOW_TO_REVIEW_VISITED_STORE_KEY,
} from "~/components/modules/userReactionButtons/WriteCommentButton";
import { QuestionAnswer, UserReactionEnum } from "~/models/questionAnswer";
import { Topic } from "~/models/topic";

export type IncreaseUserReactionProps = {
  sectionId: string;
  userReaction: UserReactionEnum;
  questionAnswerId: QuestionAnswer["id"];
};

interface UserReactionButtonsProps {
  increaseUserReactionCallback?: (
    data: IncreaseUserReactionProps
  ) => Promise<void>;
  questionAnswerId: QuestionAnswer["id"];
  sectionId: string;
  sectionSmileCount: number | undefined;
  sectionThumbsUpCount: number | undefined;
  topicSlug: Topic["slug"];
}

export function getUserReactionStoreKey({
  questionAnswerId,
  sectionId,
  type,
}: {
  questionAnswerId: QuestionAnswer["id"];
  sectionId: string;
  type: UserReactionEnum;
}): string {
  return `_qa_${questionAnswerId}_section_${type}_${sectionId}`;
}

export function getUserReactionForSectionStoreKey({
  questionAnswerId,
  sectionId,
}: {
  questionAnswerId: QuestionAnswer["id"];
  sectionId: string;
}) {
  return `_qa_${questionAnswerId}_section_${sectionId}`;
}

function setHasIncreasedToLocalStorage(key: string) {
  localStorage.setItem(key, "1");
}

function removeHasIncreasedFromLocalStorage(key: string) {
  localStorage.setItem(key, "0");
}

function getHasIncreasedFromLocalStorage(key: string) {
  const value = localStorage.getItem(key);

  if (value && value !== "0") {
    return true;
  }

  return false;
}

function incrementSectionTotalIncreaseInLocalStorage(key: string) {
  const currentNumber = getSectionTotalIncreaseFromLocalStorage(key);

  const incrementedNumber = currentNumber + 1;

  localStorage.setItem(key, incrementedNumber.toString());
}

function getSectionTotalIncreaseFromLocalStorage(key: string) {
  const currentValue = localStorage.getItem(key);

  const currentNumber = currentValue ? parseInt(currentValue) : 0;

  return currentNumber;
}

function setStatus(key: string, value: string): void {
  localStorage.setItem(key, value);
}

function getStatusFromLocalStorage(key: string): boolean {
  const value = localStorage.getItem(key);

  if (value && value !== "0") {
    return true;
  }

  return false;
}

const UserReactionButtons: React.FC<UserReactionButtonsProps> = ({
  increaseUserReactionCallback,
  questionAnswerId,
  sectionId,
  sectionSmileCount = 0,
  sectionThumbsUpCount = 0,
  topicSlug,
}) => {
  const [hasIncreasedSmile, setHasIncreasedSmile] = useState<boolean>(false);
  const [hasIncreasedThumbsUp, setHasIncreasedThumbsUp] =
    useState<boolean>(false);
  const [hasSmiled, setHasSmiled] = useState<boolean>(false);
  const [hasThumbsUpped, setHasThumbsUpped] = useState<boolean>(false);
  const [smileCount, setSmileCount] = useState<number>(sectionSmileCount);
  const [thumbsUpCount, setThumbsUpCount] =
    useState<number>(sectionThumbsUpCount);
  const [sectionTotalIncrease, setSectionTotalIncrease] = useState<number>(0);
  const [hasVisitedHowToReviewPage, setHasVisitedHowToReviewPage] =
    useState<boolean>(false);

  const smileStoreKey = getUserReactionStoreKey({
    questionAnswerId,
    sectionId,
    type: UserReactionEnum.smile,
  });

  const thumbsUpStoreKey = getUserReactionStoreKey({
    questionAnswerId,
    sectionId,
    type: UserReactionEnum.thumbsUp,
  });

  const sectionStoreKey = getUserReactionForSectionStoreKey({
    questionAnswerId,
    sectionId,
  });

  const canIncreaseCount = sectionTotalIncrease < 3;

  useEffect(() => {
    if (window?.localStorage) {
      const hasSmiled = getHasIncreasedFromLocalStorage(smileStoreKey);
      const hasThumbsUpped = getHasIncreasedFromLocalStorage(thumbsUpStoreKey);
      const totalIncrease =
        getSectionTotalIncreaseFromLocalStorage(sectionStoreKey);

      setHasSmiled(hasSmiled);
      setHasIncreasedSmile(hasSmiled);

      setHasThumbsUpped(hasThumbsUpped);
      setHasIncreasedThumbsUp(hasThumbsUpped);

      setSectionTotalIncrease(totalIncrease);
      setHasVisitedHowToReviewPage(
        getStatusFromLocalStorage(HOW_TO_REVIEW_VISITED_STORE_KEY)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adjustedSmileCount = (smileCount ?? 0) > 99 ? `99+` : smileCount;

  const adjustedThumbsUpCount =
    (thumbsUpCount ?? 0) > 99 ? `99+` : thumbsUpCount;

  const onSmileClick = async () => {
    if (hasSmiled) {
      setHasSmiled(false);
      setSmileCount((prevCount) => Math.max(prevCount - 1, 0));
      removeHasIncreasedFromLocalStorage(smileStoreKey);
      return;
    }
    try {
      setHasSmiled(true);
      setSmileCount((prevCount) => prevCount + 1);
      setHasIncreasedToLocalStorage(smileStoreKey);

      // prevent users to actually increase count multiple times by toggling button, and limit to 3 times
      if (!hasIncreasedSmile && canIncreaseCount) {
        setHasIncreasedSmile(true);
        setSectionTotalIncrease((prevCount) => prevCount + 1);
        incrementSectionTotalIncreaseInLocalStorage(sectionStoreKey);
        await increaseUserReactionCallback?.({
          questionAnswerId,
          sectionId,
          userReaction: UserReactionEnum.smile,
        });
      }
    } catch (err) {
      errorWithoutThrowInProduction({
        errorInfo: {
          questionAnswerId,
          sectionId,
          userReaction: UserReactionEnum.smile,
        },
        errorMessage: `Failed to increase smile for section.`,
      });
    }
  };

  const onThumbsUpClick = async () => {
    if (hasThumbsUpped) {
      setHasThumbsUpped(false);
      setThumbsUpCount((prevCount) => Math.max(prevCount - 1, 0));
      removeHasIncreasedFromLocalStorage(thumbsUpStoreKey);
      return;
    }
    try {
      setHasThumbsUpped(true);
      setThumbsUpCount((prevCount) => prevCount + 1);
      setHasIncreasedToLocalStorage(thumbsUpStoreKey);

      // prevent users to actually increase count multiple times by toggling button, and limit to 3 times
      if (!hasIncreasedThumbsUp && canIncreaseCount) {
        setHasIncreasedThumbsUp(true);
        setSectionTotalIncrease((prevCount) => prevCount + 1);
        incrementSectionTotalIncreaseInLocalStorage(sectionStoreKey);
        await increaseUserReactionCallback?.({
          questionAnswerId,
          sectionId,
          userReaction: UserReactionEnum.thumbsUp,
        });
      }
    } catch (err) {
      errorWithoutThrowInProduction({
        errorInfo: {
          questionAnswerId,
          sectionId,
          userReaction: UserReactionEnum.thumbsUp,
        },
        errorMessage: `Failed to increase thumbsUp for section.`,
      });
    }
  };

  const onWriteCommentClick = async (): Promise<void> => {
    if (hasVisitedHowToReviewPage) {
      return;
    }

    try {
      setHasVisitedHowToReviewPage(true);
      setStatus(HOW_TO_REVIEW_VISITED_STORE_KEY, "1");
    } catch (err) {
      errorWithoutThrowInProduction({
        errorInfo: {
          questionAnswerId,
          sectionId,
          stack: err?.stack,
          userReaction: "Write a comment",
        },
        errorMessage: `Failed to turn on visited status of how to review page`,
      });
    }
  };

  return (
    <ButtonGroup>
      <UserReactionIconButton
        className={hasSmiled ? "isClicked" : ""}
        count={adjustedSmileCount}
        iconText="😄"
        onClick={onSmileClick}
      />
      <UserReactionIconButton
        className={hasThumbsUpped ? "isClicked" : ""}
        count={adjustedThumbsUpCount}
        iconText="👍"
        onClick={onThumbsUpClick}
      />
      <UserReactionWriteCommentButton
        questionAnswerId={questionAnswerId}
        topicSlug={topicSlug}
        onClick={onWriteCommentClick}
        url={
          hasVisitedHowToReviewPage
            ? pagesPath.qa
                ._topicSlug(topicSlug)
                ._questionAnswerId(questionAnswerId)
                .review.$url()
            : pagesPath.qa
                ._topicSlug(topicSlug)
                ._questionAnswerId(questionAnswerId)
                .howToReview.$url()
        }
      />
    </ButtonGroup>
  );
};

const ButtonGroup = styled.div`
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  justify-content: flex-end;
  margin-top: 24px;
`;

export default UserReactionButtons;
